import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Checkbox,
  Divider,
  Spinner,
  Tabs,
  Tab,
  Chip,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import {
  ArrowLeft,
  Save,
  Edit,
  Image,
  Trash,
  AlertTriangle,
  Tag,
  Box,
  ShoppingCart,
} from "lucide-react";

import { menuService } from "@/services/menuService";
import { formatCurrency } from "@/utils/formatters";
import { Product, Category } from "@/types/menu";
import ProductTypeManager from "@/components/menu/ProductTypeManager";
import AddonsManager from "@/components/menu/AddonsManager";

export default function ProductDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const productId = id ? parseInt(id) : 0;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form data for editing
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    price: "",
    category_ids: [] as number[],
    is_featured: false,
    unavailable: false,
    image_url: "",
  });

  // Delete confirmation modal
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (productId) {
      fetchProductData();
      fetchCategories();
    }
  }, [productId]);

  const fetchProductData = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await menuService.getProductById(productId);

      setProduct(data);

      // Initialize form data
      setFormData({
        name: data.name || "",
        code: data.code || "",
        description: data.description || "",
        price: (data.price / 100).toFixed(2), // Convert cents to dollars for display
        category_ids: data.categories ? data.categories.map((c) => c.id) : [],
        is_featured: data.is_featured || false,
        unavailable: data.unavailable || false,
        image_url: data.image_url || "",
      });
    } catch (err) {
      console.error("Error fetching product:", err);
      setError("Failed to load product data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await menuService.getCategories();

      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(
      (e.target as HTMLSelectElement).selectedOptions,
      (option) => parseInt(option.value),
    );

    setFormData({
      ...formData,
      category_ids: selectedOptions,
    });
  };

  const handleSave = async () => {
    try {
      // Validate form
      if (!formData.name.trim()) {
        setError("Product name is required");

        return;
      }

      if (!formData.code.trim()) {
        setError("Product code is required");

        return;
      }

      if (!formData.price.trim() || isNaN(parseFloat(formData.price))) {
        setError("Valid price is required");

        return;
      }

      if (formData.category_ids.length === 0) {
        setError("At least one category must be selected");

        return;
      }

      setSaving(true);
      setError("");

      // Convert price to cents for API
      const priceInCents = Math.round(parseFloat(formData.price) * 100);

      const productData = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        price: priceInCents,
        category_ids: formData.category_ids,
        is_featured: formData.is_featured,
        unavailable: formData.unavailable,
        image_url: formData.image_url || undefined,
      };

      await menuService.updateProduct(productId, productData);

      // Refresh product data
      await fetchProductData();
      setEditMode(false);
    } catch (err) {
      console.error("Error updating product:", err);
      setError("Failed to update product");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);

      await menuService.deleteProduct(productId);

      // Navigate back to products list
      navigate("/menu/products");
    } catch (err) {
      console.error("Error deleting product:", err);
      setError("Failed to delete product");
      setDeleting(false);
      onDeleteClose();
    }
  };

  const handleBack = () => {
    navigate("/menu/products");
  };

  const handleAvailabilityToggle = async () => {
    try {
      setLoading(true);

      if (!product) return;

      await menuService.updateProductAvailability(
        productId,
        {unavailable: product.unavailable},
      );

      // Refresh product data
      await fetchProductData();
    } catch (err) {
      console.error("Error updating availability:", err);
      setError("Failed to update availability");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadImage = () => {
    // Implementation for image upload would go here
    // This is a placeholder for the actual implementation

    // For now, set a placeholder URL
    setFormData({
      ...formData,
      image_url: "https://example.com/placeholder.jpg",
    });
  };

  if (loading && !product) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Button variant="light" onPress={handleBack}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-2xl font-bold">Error</h1>
        </div>
        <Card className="p-6 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-danger mb-4" />
          <h2 className="text-xl font-semibold mb-2">Failed to Load Product</h2>
          <p className="text-default-500 mb-4">{error}</p>
          <Button color="primary" onPress={handleBack}>
            Back to Products
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-2">
          <Button isIconOnly variant="light" onPress={handleBack}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-2xl font-bold">
            {product?.name}
            {product?.unavailable && (
              <Chip className="ml-2" color="danger" variant="flat">
                Unavailable
              </Chip>
            )}
            {product?.is_featured && (
              <Chip className="ml-2" color="success" variant="flat">
                Featured
              </Chip>
            )}
          </h1>
        </div>
        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button
                color="primary"
                variant="flat"
                onPress={() => setEditMode(false)}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                isLoading={saving}
                startContent={<Save size={16} />}
                onPress={handleSave}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button
                color={product?.unavailable ? "success" : "danger"}
                variant="flat"
                onPress={handleAvailabilityToggle}
              >
                {product?.unavailable ? "Mark Available" : "Mark Unavailable"}
              </Button>
              <Button
                color="danger"
                startContent={<Trash size={16} />}
                variant="flat"
                onPress={onDeleteOpen}
              >
                Delete
              </Button>
              <Button
                color="primary"
                startContent={<Edit size={16} />}
                onPress={() => setEditMode(true)}
              >
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs>
        <Tab key="details" title="Details">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
            {/* Left column - Details */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6 space-y-6">
                <h2 className="text-xl font-semibold">Product Details</h2>

                {error && (
                  <div className="bg-danger-50 text-danger p-3 rounded-md">
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  {editMode ? (
                    <>
                      <Input
                        isRequired
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                      <Input
                        isRequired
                        label="Code"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                      />
                      <Textarea
                        label="Description"
                        minRows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                      />
                      <Input
                        isRequired
                        label="Price ($)"
                        min="0"
                        name="price"
                        startContent={
                          <div className="pointer-events-none flex items-center">
                            <span className="text-default-400 text-small">
                              $
                            </span>
                          </div>
                        }
                        step="0.01"
                        type="number"
                        value={formData.price}
                        onChange={handleInputChange}
                      />
                      <Select
                        isRequired
                        label="Categories"
                        selectedKeys={formData.category_ids.map((id) =>
                          id.toString(),
                        )}
                        selectionMode="multiple"
                        onChange={handleCategoryChange}
                      >
                        {categories.map((category) => (
                          <SelectItem key={category.id} textValue={category.name}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </Select>
                      <div className="flex flex-col gap-2">
                        <Checkbox
                          isSelected={formData.is_featured}
                          name="is_featured"
                          onValueChange={(checked) =>
                            setFormData({ ...formData, is_featured: checked })
                          }
                        >
                          Featured Product
                        </Checkbox>
                        <Checkbox
                          isSelected={formData.unavailable}
                          name="unavailable"
                          onValueChange={(checked) =>
                            setFormData({ ...formData, unavailable: checked })
                          }
                        >
                          Temporarily Unavailable
                        </Checkbox>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <h3 className="text-sm font-medium text-default-600">
                            Name
                          </h3>
                          <p>{product?.name}</p>
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-sm font-medium text-default-600">
                            Code
                          </h3>
                          <p>{product?.code}</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-sm font-medium text-default-600">
                          Description
                        </h3>
                        <p className="text-sm">
                          {product?.description || "No description provided"}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <h3 className="text-sm font-medium text-default-600">
                            Price
                          </h3>
                          <p className="text-lg font-semibold">
                            {formatCurrency(product?.price || 0)}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-sm font-medium text-default-600">
                            Categories
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {product?.categories?.map((category) => (
                              <Chip key={category.id} size="sm">
                                {category.name}
                              </Chip>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-sm font-medium text-default-600">
                          Status
                        </h3>
                        <div className="flex gap-2">
                          {product?.unavailable ? (
                            <Chip color="danger" variant="flat">
                              Unavailable
                            </Chip>
                          ) : (
                            <Chip color="success" variant="flat">
                              Available
                            </Chip>
                          )}

                          {product?.is_featured && (
                            <Chip color="warning" variant="flat">
                              Featured
                            </Chip>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Product Types</h2>
                <ProductTypeManager
                  productId={productId}
                  onUpdate={fetchProductData}
                />
              </Card>

              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">
                  Add-ons & Options
                </h2>
                {product && (
                  <AddonsManager
                    productId={productId}
                    productTypeId={product.product_types?.[0]?.id}
                    readOnly={!editMode}
                    onAddonsChange={fetchProductData}
                  />
                )}
              </Card>
            </div>

            {/* Right column - Image and stats */}
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Product Image</h2>

                {editMode ? (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center">
                    {formData.image_url ? (
                      <div className="space-y-2">
                        <img
                          alt="Product"
                          className="max-w-full h-auto mx-auto rounded-md"
                          src={formData.image_url}
                        />
                        <Button
                          color="danger"
                          variant="flat"
                          onPress={() =>
                            setFormData({ ...formData, image_url: "" })
                          }
                        >
                          Remove Image
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Image className="w-12 h-12 text-default-300 mx-auto" />
                        <p className="text-default-500">No image uploaded</p>
                        <Button
                          color="primary"
                          variant="flat"
                          onPress={handleUploadImage}
                        >
                          Upload Image
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    {product?.image_url ? (
                      <img
                        alt={product.name}
                        className="max-w-full h-auto mx-auto rounded-md"
                        src={product.image_url}
                      />
                    ) : (
                      <div className="border rounded-lg p-8">
                        <Image className="w-12 h-12 text-default-300 mx-auto" />
                        <p className="text-default-500 mt-2">
                          No image available
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              <Card className="p-6 space-y-4">
                <h2 className="text-xl font-semibold">Product Stats</h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5 text-default-500" />
                      <span>Orders</span>
                    </div>
                    <span className="font-semibold">-</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Box className="w-5 h-5 text-default-500" />
                      <span>In Stock</span>
                    </div>
                    <span className="font-semibold">-</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tag className="w-5 h-5 text-default-500" />
                      <span>Revenue</span>
                    </div>
                    <span className="font-semibold">-</span>
                  </div>
                </div>

                <Divider />

                <p className="text-sm text-center text-default-500">
                  Sales statistics will be displayed here once available.
                </p>
              </Card>
            </div>
          </div>
        </Tab>
        <Tab key="addons" title="Add-ons & Options">
          <Card className="p-6 mt-4">
            {product && (
              <AddonsManager
                productId={productId}
                productTypeId={product.product_types?.[0]?.id}
                readOnly={!editMode}
                onAddonsChange={fetchProductData}
              />
            )}
          </Card>
        </Tab>

        <Tab key="analytics" title="Analytics">
          <Card className="p-6 mt-4">
            <p className="text-center">
              Product analytics and sales data would be displayed here.
            </p>
          </Card>
        </Tab>
      </Tabs>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-danger">Delete Product</ModalHeader>
              <ModalBody>
                <div className="flex flex-col items-center gap-4">
                  <AlertTriangle className="w-12 h-12 text-danger" />
                  <div className="text-center">
                    <h3 className="text-lg font-medium">
                      Are you sure you want to delete this product?
                    </h3>
                    <p className="text-sm text-default-500 mt-2">
                      This action cannot be undone. This will permanently delete
                      the product "{product?.name}" and all associated types and
                      add-ons.
                    </p>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="danger"
                  isLoading={deleting}
                  onPress={handleDelete}
                >
                  Delete Product
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
